// Prefix over EVERY season's Medfly evidence for one org+field. The onSettled
// invalidation after an attention/activity write spans all seasons (the
// authoritative readback, never a local mutation, decides whether an open
// follow-up disappeared). The per-season key below extends this exact prefix,
// so prefix invalidation always matches the season-scoped query keys.
const medflyMonitoringByFieldKey = (orgId: string, fieldId: string) =>
  ['fieldwatch-v2', 'medfly-monitoring', orgId, fieldId] as const;

export const queryKeys = {
  devices: {
    all: ['devices'] as const,
    list: (orgId: string) => ['devices', orgId] as const,
    detail: (id: string) => ['devices', 'detail', id] as const,
    twin: (id: string) => ['devices', 'twin', id] as const,
    sensors: (id: string) => ['devices', 'sensors', id] as const,
  },
  fields: {
    all: ['fields'] as const,
    list: (orgId: string) => ['fields', orgId] as const,
    detail: (id: string) => ['fields', 'detail', id] as const,
    ndvi: (id: string) => ['fields', 'ndvi', id] as const,
  },
  fieldSeasons: {
    list: (fieldId: string) => ['fieldSeasons', fieldId] as const,
    chartData: (fieldId: string, seasonId: string) =>
      ['fieldSeasons', fieldId, seasonId, 'chart'] as const,
    // Full request identity: the projection depends on the active season's
    // canonical variety and the org that scoped the request, so both belong in
    // the key — a variety correction or org switch on the same field/season
    // must miss the cache instead of serving the previous identity's result.
    // `fieldId` stays at index 1 so `list(fieldId)` prefix invalidation still
    // reaches these entries.
    gddProjection: (orgId: string, fieldId: string, seasonId: string, varietyToken: string) =>
      ['fieldSeasons', fieldId, seasonId, 'gdd-projection', orgId, varietyToken] as const,
    pestRiskSummary: (fieldSeasonId: string) =>
      ['fieldSeasons', fieldSeasonId, 'pest-risk-summary'] as const,
    events: (fieldSeasonId: string) => ['fieldSeasons', fieldSeasonId, 'events'] as const,
    treatments: (fieldSeasonId: string) => ['fieldSeasons', fieldSeasonId, 'treatments'] as const,
  },
  seasonAnalytics: {
    trends: (orgId: string) => ['seasonAnalytics', 'trends', orgId] as const,
    fertilizerAnalysis: (orgId: string) => ['seasonAnalytics', 'fertilizer', orgId] as const,
    varietyRecommendations: (orgId: string) =>
      ['seasonAnalytics', 'variety-recommendations', orgId] as const,
    sowingPrediction: (orgId: string, cropType: string) =>
      ['seasonAnalytics', 'sowing-prediction', orgId, cropType] as const,
    dashboard: (orgId: string) => ['seasonAnalytics', 'dashboard', orgId] as const,
  },
  alarms: {
    filtered: (orgId: string, filters?: Record<string, string>) =>
      ['alarms', orgId, filters] as const,
    // Single-alarm canonical detail (drawer re-read). Rooted at the `alarms`
    // namespace so the shared alarm-action invalidation (`all`) also marks it
    // stale; the `'detail'` segment keeps it distinct from the list keys.
    detail: (orgId: string, alarmId: string) => ['alarms', orgId, 'detail', alarmId] as const,
    all: ['alarms'] as const,
    list: (orgId: string) => ['alarms', orgId] as const,
  },
  tgAlarms: {
    all: ['tg', 'alarms'] as const,
    paginatedAll: ['tg', 'alarms-all'] as const,
    paginated: (orgId: string, pageSize: number) => ['tg', 'alarms-all', orgId, pageSize] as const,
  },
  sensors: {
    timeseries: (deviceId: string, key: string) =>
      ['sensors', 'timeseries', deviceId, key] as const,
    history: (compId: string, range: string, interval: string) =>
      ['sensors', 'history', compId, range, interval] as const,
  },
  fieldSensors: {
    byField: (fieldId: string) => ['fieldSensors', fieldId] as const,
  },
  fieldTwin: {
    byField: (fieldId: string) => ['fieldTwin', fieldId] as const,
  },
  fieldSummary: {
    byField: (id: string) => ['fieldSummary', id] as const,
  },
  fieldWeather: {
    byField: (id: string) => ['fieldWeather', id] as const,
  },
  fieldAgronomic: {
    byField: (id: string) => ['fieldAgronomic', id] as const,
  },
  agronomics: {
    fieldEntrySummary: (orgId: string, fieldId: string) =>
      ['fieldwatch-v2', 'agronomics', 'field-entry-summary', orgId, fieldId] as const,
  },
  fieldwatchV2: {
    attention: (orgId: string) => ['fieldwatch-v2', 'attention', orgId] as const,
    overview: (orgId: string) => ['fieldwatch-v2', 'overview', orgId] as const,
    // Shared org-scoped V2 field-list identity. Every successful season write
    // must invalidate this family for its request's ORIGIN org (explicit
    // argument — never an ambient re-read), or a cached active_season
    // projection (e.g. variety_id=null) survives the repair for staleTime.
    orgFields: (orgId: string) => ['fieldwatch-v2', 'org-fields', orgId] as const,
    // Trap-count readback list (ADR-070 Slice 1). Keyed to the active season so
    // a season transition never serves the prior season's rows from cache.
    trapCounts: (orgId: string, fieldId: string, fieldSeasonId: string) =>
      ['fieldwatch-v2', 'trap-counts', orgId, fieldId, fieldSeasonId] as const,
    // Org+field prefix over every season's Medfly evidence — the invalidation
    // contract shared by the attention/activity write onSettled hook.
    medflyMonitoringByField: medflyMonitoringByFieldKey,
    // Backend-owned Medfly evidence state. All three scope identifiers are
    // request identity: an org, field, or season transition must miss cache.
    // Structurally extends the org+field prefix so prefix invalidation matches.
    medflyMonitoring: (orgId: string, fieldId: string, fieldSeasonId: string) =>
      [...medflyMonitoringByFieldKey(orgId, fieldId), fieldSeasonId] as const,
  },
  activities: {
    byField: (id: string) => ['activities', id] as const,
  },
  intelligence: {
    fieldTimeline: (fieldId: string, limit: number) =>
      ['intelligence', 'fieldTimeline', fieldId, limit] as const,
  },
  ndvi: {
    byField: (fieldId: string) => ['ndvi', fieldId] as const,
    overlayMeta: (fieldId: string) => ['ndvi', 'overlay-meta', fieldId] as const,
  },
  stock: {
    all: ['stock'] as const,
    byOrg: (orgId: string) => ['stock', orgId] as const,
  },
  riskModels: {
    all: ['riskModels'] as const,
  },
  fieldRiskMap: {
    byOrg: (orgId: string) => ['fieldRiskMap', orgId] as const,
  },
  farms: {
    all: ['farms'] as const,
    list: (orgId: string) => ['farms', orgId] as const,
    // Single-farm canonical detail. Org-scoped (mirrors `alarms.detail` and the
    // sibling `twin` key) so the same farm UUID reused across separate orgs can
    // never collide in cache. Rooted in the `farms` namespace so the shared farm
    // invalidation (`all`) still marks it stale.
    detail: (orgId: string, id: string) => ['farms', orgId, 'detail', id] as const,
    // Farm-level Digital Twin aggregate (detail-page status summary). Rooted in
    // the `farms` namespace and keyed by org + farm id so it is distinct from
    // the list/detail caches yet covered by the shared farm invalidation.
    twin: (orgId: string, farmId: string) => ['farms', orgId, 'twin', farmId] as const,
  },
  assets: {
    all: ['assets'] as const,
    list: (orgId: string) => ['assets', orgId] as const,
    detail: (id: string) => ['assets', 'detail', id] as const,
    state: (id: string) => ['assets', 'state', id] as const,
    commands: (id: string) => ['assets', 'commands', id] as const,
  },
  assetTypes: {
    all: ['asset-types'] as const,
    list: (orgId: string) => ['asset-types', orgId] as const,
  },
  zones: {
    all: ['zones'] as const,
    list: (orgId: string) => ['zones', orgId] as const,
    detail: (id: string) => ['zones', 'detail', id] as const,
    twin: (id: string) => ['zones', 'twin', id] as const,
    // Field-scoped zone read (M4 field→zone assignment). The org id is in the key
    // so an org switch on the same field can never serve the prior tenant's zones,
    // and the field id is in the key so field A and field B never share one cache
    // entry (the defect the broad `useZones` static ['zones'] key has today).
    // Extends the ['zones'] prefix, so the existing zone create/update/delete
    // invalidation of `zones.all` still cascades to every field-scoped entry.
    byField: (orgId: string, fieldId: string) => ['zones', orgId, 'by-field', fieldId] as const,
  },
  rules: {
    all: ['rules'] as const,
    list: (orgId: string) => ['rules', orgId] as const,
  },
  members: {
    all: ['members'] as const,
  },
  components: {
    all: ['components', 'all'] as const,
  },
  org: {
    detail: (id: string) => ['org', id] as const,
  },
  provisionTokens: {
    all: ['provisionTokens'] as const,
    byDevice: (deviceId: string) => ['provisionTokens', deviceId] as const,
    // Org-wide read-only provision-token diagnostic counters (GOAL-UI-000 PR-AA;
    // GET /orgs/{org_id}/provision-tokens/status). Admin/owner-gated, so the key
    // carries org_id.
    status: (orgId: string) => ['provisionTokens', 'status', orgId] as const,
  },
  provisionPreRegistrations: {
    all: ['provisionPreRegistrations'] as const,
    // Org-wide read-only device claim-progress diagnostic (GOAL-UI-000 PR-AN;
    // GET /orgs/{org_id}/provision/pre-registrations). Admin/owner-gated, so the
    // key carries org_id.
    status: (orgId: string) => ['provisionPreRegistrations', 'status', orgId] as const,
  },
  me: {
    current: ['me'] as const,
  },
  preferences: {
    all: (userId: string) => ['preferences', userId] as const,
    theme: (userId: string) => ['preferences', userId, 'theme'] as const,
    ui: (userId: string) => ['preferences', userId, 'ui'] as const,
    pageViews: (userId: string) => ['preferences', userId, 'pageViews'] as const,
    // Timezone-SSOT B2: account-global display-timezone preference. Keyed by the
    // IMMUTABLE token subject (never an ambient org) so a same-tab account switch
    // — which leaves the shared ['me'] cache in place — can never surface the
    // prior identity's preference. Extends the `preferences.all` prefix.
    displayTimezone: (userId: string) => ['preferences', userId, 'displayTimezone'] as const,
  },
  dashboards: {
    all: (userId: string) => ['dashboards', userId] as const,
    detail: (userId: string, dashboardId: string) => ['dashboards', userId, dashboardId] as const,
  },
  events: {
    all: ['events'] as const,
    analytics: (orgId: string, from: string, to: string) =>
      ['events', 'analytics', orgId, from, to] as const,
  },
  security: {
    events: (orgId: string, params: { hours?: number; severity?: string; search?: string }) =>
      ['security', 'events', orgId, params] as const,
    summary: (orgId: string, hours: number) => ['security', 'summary', orgId, hours] as const,
  },
  analytics: {
    data: (orgId: string, entityId: string, metric: string, hours: number) =>
      ['analytics', orgId, entityId, metric, hours] as const,
    telemetryMetadata: (orgId: string) => ['analytics', 'telemetry-metadata', orgId] as const,
  },
  schedules: {
    list: (deviceId: string) => ['schedules', deviceId] as const,
    irrigationLog: (deviceId: string) => ['schedules', 'irrigation-log', deviceId] as const,
  },
  prognostics: {
    irrigationForecast: (orgId: string, zoneId: string) =>
      ['prognostics', 'irrigation', orgId, zoneId] as const,
    pumpHealth: (orgId: string) => ['prognostics', 'pump-health', orgId] as const,
    weatherAlerts: (orgId: string) => ['prognostics', 'weather-alerts', orgId] as const,
  },
  accessGrants: {
    list: (orgId: string) => ['access-grants', orgId] as const,
    logs: (orgId: string) => ['access-logs', orgId] as const,
  },
  sos: {
    health: (orgId: string) => ['sos', 'health', orgId] as const,
    federation: (orgId: string) => ['sos', 'federation', orgId] as const,
    // Lazy per-partner federation weather aggregate (GET /orgs/{org}/federation/
    // {partner_org_id}/weather). Scoped by org AND the selected partner so the
    // disclosed read never serves another partner's cached summary.
    federationWeather: (orgId: string, partnerId: string) =>
      ['sos', 'federation-weather', orgId, partnerId] as const,
    valueReport: (orgId: string, year: number, month: number) =>
      ['sos', 'value-report', orgId, year, month] as const,
    // System-level capability topology summary (GET /api/v1/sos/capabilities).
    // No org scope — public aggregate, so the key carries no org_id.
    capabilities: ['sos', 'capabilities'] as const,
    // System-level constituent-system topology (GET /api/v1/systems). No org
    // scope — public platform-wide aggregate, so the key carries no org_id.
    systems: ['sos', 'systems'] as const,
    // System-level public API readiness signal (GET /health). A liveness/
    // readiness probe with no org scope and no auth-derived segment, so the key
    // carries no org_id.
    apiReadiness: ['sos', 'api-readiness'] as const,
  },
  soilAnalysis: {
    all: ['soil-analysis'] as const,
    list: (orgId: string) => ['soil-analysis', orgId] as const,
    detail: (setId: string) => ['soil-analysis', 'detail', setId] as const,
    parameters: (setId: string) => ['soil-analysis', 'parameters', setId] as const,
    map: (setId: string, parameter: string, depth: number) =>
      ['soil-analysis', 'map', setId, parameter, depth] as const,
    classifications: (orgId: string) => ['soil-analysis', 'classifications', orgId] as const,
  },
  leafAnalysis: {
    all: ['leaf-analysis'] as const,
    sets: (orgId: string) => ['leaf-analysis', 'sets', orgId] as const,
    detail: (setId: string) => ['leaf-analysis', 'detail', setId] as const,
    summary: (setId: string, fieldId?: string) =>
      ['leaf-analysis', 'summary', setId, fieldId ?? null] as const,
    fields: (setId: string) => ['leaf-analysis', 'fields', setId] as const,
    // V2 read-only leaf sample evidence (ADR-UI-039 PR-H). Distinct fieldwatch-v2
    // namespace so the masked lean views never collide with the legacy raw-shaped
    // leafAnalysis.sets cache (same endpoint, different cached shape).
    sampleSets: (orgId: string) =>
      ['fieldwatch-v2', 'leaf-analysis', 'sample-sets', orgId] as const,
    samples: (orgId: string, setId: string) =>
      ['fieldwatch-v2', 'leaf-analysis', 'samples', orgId, setId] as const,
  },
  workspaceViews: {
    list: (orgId: string) => ['workspace-views', orgId] as const,
    detail: (orgId: string, viewId: string) => ['workspace-views', orgId, viewId] as const,
  },
  mapData: {
    all: ['map-data'] as const,
  },
  widgetSensor: (compId: string, range: string, agg: string) =>
    ['widget-sensor', compId, range, agg] as const,
  calendarSensor: (compId: string, year: number) => ['calendar-sensor', compId, year] as const,
  sensorsList: (orgId: string) => ['sensors-list', orgId] as const,
  workflows: {
    all: ['workflows'] as const,
    list: (orgId: string) => ['workflows', orgId] as const,
    detail: (id: string) => ['workflows', 'detail', id] as const,
    templates: (orgId: string) => ['workflows', 'templates', orgId] as const,
    executions: (ruleId: string) => ['workflows', 'executions', ruleId] as const,
  },
  cropCatalog: {
    all: ['cropCatalog'] as const,
    withVarieties: ['cropCatalog', 'with-varieties'] as const,
  },
  // V2 shared agronomy catalog (ADR-UI-039). GLOBAL reference reads — identical
  // bytes for every org — so the keys are intentionally org-agnostic and the
  // cache is shared across tenants (no orgId dimension).
  agronomyCatalog: {
    all: ['fieldwatch-v2', 'agronomy-catalog'] as const,
    crops: ['fieldwatch-v2', 'agronomy-catalog', 'crops'] as const,
    crop: (cropId: string) => ['fieldwatch-v2', 'agronomy-catalog', 'crop', cropId] as const,
    pests: ['fieldwatch-v2', 'agronomy-catalog', 'pests'] as const,
    pest: (pestId: string) => ['fieldwatch-v2', 'agronomy-catalog', 'pest', pestId] as const,
    riskSummary: (cropId: string) =>
      ['fieldwatch-v2', 'agronomy-catalog', 'risk-summary', cropId] as const,
    riskRows: (cropId: string) =>
      ['fieldwatch-v2', 'agronomy-catalog', 'risk-rows', cropId] as const,
  },
  // V2 read-only device-model catalog evidence (ADR-UI-038 PR-I). Distinct
  // fieldwatch-v2 namespace so the masked lean views never collide with the
  // legacy raw-shaped `setup.deviceModels` / tg-provision caches (same list
  // endpoint, different cached shape). `list` is org-membership-scoped, so its
  // key carries orgId. `compatibleSensors` reads the GLOBAL
  // /device-models/{id}/compatible-sensors route — identical bytes for every org
  // — so it is keyed by modelId only (no org dimension).
  deviceModelCatalog: {
    all: ['fieldwatch-v2', 'device-models'] as const,
    list: (orgId: string) => ['fieldwatch-v2', 'device-models', 'list', orgId] as const,
    // Org-scoped authoritative DETAIL (GOAL-UI-000 PR-N). Lazy per opened model;
    // carries orgId (org-membership-scoped) + modelId.
    detail: (orgId: string, modelId: string) =>
      ['fieldwatch-v2', 'device-models', 'detail', orgId, modelId] as const,
    compatibleSensors: (modelId: string) =>
      ['fieldwatch-v2', 'device-models', 'compatible-sensors', modelId] as const,
    // Global hardware-catalog summary (GET /device-models, no org context). Bounded
    // aggregate/provenance evidence; org-agnostic, so the key carries no orgId.
    globalSummary: () => ['fieldwatch-v2', 'device-models', 'global-summary'] as const,
  },
  // V2 read-only field-template catalog evidence (GOAL-UI-000 PR-J). Org-scoped
  // list read (org + platform-default templates), so its key carries orgId. Only
  // the list is keyed — the org-scoped DETAIL returns the identical
  // FieldTemplateRead, so it is intentionally never fetched (a dead read).
  fieldTemplateCatalog: {
    all: ['fieldwatch-v2', 'field-templates'] as const,
    list: (orgId: string) => ['fieldwatch-v2', 'field-templates', 'list', orgId] as const,
  },
  // V2 read-only capability discovery evidence (GOAL-UI-000 PR-K). Org-scoped list
  // read reduced to a bounded aggregate, so its key carries orgId. The system-level
  // no-auth /capabilities aggregate is cross-org and intentionally never fetched.
  capabilityDiscovery: {
    all: ['fieldwatch-v2', 'capabilities'] as const,
    summary: (orgId: string) => ['fieldwatch-v2', 'capabilities', 'summary', orgId] as const,
  },
  // V2 read-only canonical analytics evidence (GOAL-UI-000 PR-L). Org-scoped
  // series discovery + a lazy per-series observation history. The history key
  // carries property_code + subject_ref (the two query params that identify one
  // whitelisted series), so each selected series caches independently.
  reportsAnalytics: {
    all: ['fieldwatch-v2', 'reports-analytics'] as const,
    series: (orgId: string) => ['fieldwatch-v2', 'reports-analytics', 'series', orgId] as const,
    history: (orgId: string, propertyCode: string, subjectRef: string) =>
      ['fieldwatch-v2', 'reports-analytics', 'history', orgId, propertyCode, subjectRef] as const,
  },
  // V2 read-only fleet evidence (GOAL-UI-000 PR-O). Org-scoped fleet topology +
  // lifecycle-transition activity + recurring alarm patterns co-rendered on
  // /v2/sensors. Distinct fieldwatch-v2 namespace so these masked views never
  // collide with the legacy raw-shaped `fleet.*` health/trend/battery caches.
  fleetEvidence: {
    all: ['fieldwatch-v2', 'fleet-evidence'] as const,
    topology: (orgId: string) => ['fieldwatch-v2', 'fleet-evidence', 'topology', orgId] as const,
    lifecycleActivity: (orgId: string) =>
      ['fieldwatch-v2', 'fleet-evidence', 'lifecycle-activity', orgId] as const,
    alarmPatterns: (orgId: string) =>
      ['fieldwatch-v2', 'fleet-evidence', 'alarm-patterns', orgId] as const,
    dailySummary: (orgId: string) =>
      ['fieldwatch-v2', 'fleet-evidence', 'daily-summary', orgId] as const,
  },
  // V2 read-only device component-inventory drift evidence (GOAL-UI-000 PR-P) on
  // /v2/edge-offline. Org-wide drift summary (eager) + lazy per-device drift detail.
  deviceDrift: {
    all: ['fieldwatch-v2', 'device-drift'] as const,
    summary: (orgId: string) => ['fieldwatch-v2', 'device-drift', 'summary', orgId] as const,
    detail: (orgId: string, deviceId: string) =>
      ['fieldwatch-v2', 'device-drift', 'detail', orgId, deviceId] as const,
  },
  // V2 read-only device lifecycle audit evidence (GOAL-UI-000 PR-T) on
  // /v2/edge-offline. Device list is already-covered context; audit history is
  // lazy per-device detail. No lifecycle write/control.
  deviceLifecycle: {
    all: ['fieldwatch-v2', 'device-lifecycle'] as const,
    devices: (orgId: string) => ['fieldwatch-v2', 'device-lifecycle', 'devices', orgId] as const,
    history: (orgId: string, deviceId: string) =>
      ['fieldwatch-v2', 'device-lifecycle', 'history', orgId, deviceId] as const,
  },
  // V2 read-only device twin-history evidence (GOAL-UI-000 PR-AC) on
  // /v2/edge-offline. The device list is already-covered context (reused from the
  // lifecycle panel's hook, so it shares one eager GET /devices and adds no new
  // coverage); twin_state_history is a lazy per-device detail. comp_id and the raw
  // JSONB value never enter the view model or this cache.
  deviceTwinHistory: {
    all: ['fieldwatch-v2', 'device-twin-history'] as const,
    history: (orgId: string, deviceId: string) =>
      ['fieldwatch-v2', 'device-twin-history', 'history', orgId, deviceId] as const,
  },
  // V2 read-only device twin-deltas aggregate evidence (GOAL-UI-000 PR-AX) on
  // /v2/edge-offline. A single eager fleet-aggregate GET; only the bounded counts
  // (total / pending / oldest-pending-age) reach the view — the per-device rows
  // (device_id, name, raw delta_state) are structurally dropped in the adapter.
  deviceTwinDeltas: {
    all: ['fieldwatch-v2', 'device-twin-deltas'] as const,
    summary: (orgId: string) => ['fieldwatch-v2', 'device-twin-deltas', 'summary', orgId] as const,
  },
  // V2 read-only zone soil-moisture diagnostic evidence (GOAL-UI-000 PR-W) on the
  // zone detail route. ADR-UI-071 legacy-risk: a legacy moisture diagnostic read,
  // NOT an ADR-071 source-rule water-status score. Lazy per-zone detail only; no
  // compute/recommendation/irrigation write.
  zoneDiagnostic: {
    all: ['fieldwatch-v2', 'zone-diagnostic'] as const,
    detail: (orgId: string, zoneId: string) =>
      ['fieldwatch-v2', 'zone-diagnostic', 'detail', orgId, zoneId] as const,
  },
  // V2 read-only connector integration-health evidence (GOAL-UI-000 PR-Q) on
  // /v2/settings. Admin-gated org connector list (eager only when admin) + lazy
  // per-connector authoritative detail. `access` derives the owner/admin gate
  // from the session /auth/me — keyed by user AND org so a same-tab user switch
  // never serves the prior user's role from cache.
  connectors: {
    all: ['fieldwatch-v2', 'connectors'] as const,
    access: (orgId: string, userId: string) =>
      ['fieldwatch-v2', 'connectors', 'access', orgId, userId] as const,
    list: (orgId: string) => ['fieldwatch-v2', 'connectors', 'list', orgId] as const,
    detail: (orgId: string, connectorId: string) =>
      ['fieldwatch-v2', 'connectors', 'detail', orgId, connectorId] as const,
  },
  // V2 read-only telemetry segment-discovery metadata evidence (GOAL-UI-000 PR-R)
  // co-rendered on /v2/sensors. A device picker is sourced from the org devices
  // list; per-device telemetry metadata is fetched lazily only after a real
  // device is selected (the backend requires device_id, so it never fires unkeyed).
  telemetryMetadata: {
    all: ['fieldwatch-v2', 'telemetry-metadata'] as const,
    devices: (orgId: string) => ['fieldwatch-v2', 'telemetry-metadata', 'devices', orgId] as const,
    metadata: (orgId: string, deviceId: string) =>
      ['fieldwatch-v2', 'telemetry-metadata', 'metadata', orgId, deviceId] as const,
  },
  // V2 read-only sensor component detail evidence (GOAL-UI-000 PR-S) co-rendered on
  // /v2/sensors. The component list reuses the already-covered devices/sensors/all
  // read; per-component authoritative detail is fetched lazily only after a row is
  // expanded (comp_id is a path param / React key, never a label).
  componentDetail: {
    all: ['fieldwatch-v2', 'component-detail'] as const,
    components: (orgId: string) =>
      ['fieldwatch-v2', 'component-detail', 'components', orgId] as const,
    detail: (orgId: string, compId: string) =>
      ['fieldwatch-v2', 'component-detail', 'detail', orgId, compId] as const,
    // PR-AE bounded telemetry-history summary, keyed by the history call's path
    // params (org + device_id + sensor_key); lazy per expanded component row.
    sensorHistory: (orgId: string, deviceId: string, sensorKey: string) =>
      ['fieldwatch-v2', 'component-detail', 'sensor-history', orgId, deviceId, sensorKey] as const,
  },
  setup: {
    status: ['setup', 'status'] as const,
    linkTypes: ['setup', 'link-types'] as const,
    infraLinks: (filters?: Record<string, unknown>) =>
      ['setup', 'infra-links', filters ?? {}] as const,
    deviceSlots: (deviceId: string) => ['setup', 'device-slots', deviceId] as const,
    deviceModels: ['setup', 'device-models'] as const,
  },
  lorawan: {
    provision: ['lorawan', 'provision'] as const,
  },
  // GOAL-UI-000 PR-AO: V2 automation stale-rule ops diagnostic. `access` is
  // session+org scoped (the membership role comes from /auth/me, so the key
  // carries both user and org); `list` is org scoped.
  staleRules: {
    access: (orgId: string, userId: string) =>
      ['fieldwatch-v2', 'automation', 'stale-rules', 'access', orgId, userId] as const,
    list: (orgId: string) => ['fieldwatch-v2', 'automation', 'stale-rules', 'list', orgId] as const,
  },
  // GOAL-UI-000 PR-BH: V2 automation policy-push-queue ops signal. Only the lazy
  // `list` read; the owner/admin gate reuses the PR-AO staleRules.access key (same
  // /auth/me membership-role check) so the page issues one shared access read.
  policyPushQueue: {
    list: (orgId: string) => ['fieldwatch-v2', 'automation', 'policy-push-queue', 'list', orgId] as const,
  },
  // Scout94 ADR-UI-102: V2 field map-layer default-facade preview. `access` is
  // session+org scoped (the internal `is_platform_admin` gate comes from
  // /auth/me, so the key carries both user and org). `fieldDefault` keys the
  // create-or-recover issuance by org + field + render format, so each opened
  // field caches its own bounded handle independently (no cross-field bleed).
  mapLayers: {
    access: (orgId: string, userId: string) =>
      ['fieldwatch-v2', 'map-layers', 'access', orgId, userId] as const,
    fieldDefault: (orgId: string, fieldId: string, format: string) =>
      ['fieldwatch-v2', 'map-layers', 'field-default', orgId, fieldId, format] as const,
  },
  // GOAL-UI-000 PR-AP: V2 platform onboarding-summary diagnostic. Both keys are
  // session scoped by USER only (no org): the read is the FLEET-WIDE
  // platform-admin endpoint GET /platform/orgs/onboarding-summary, and the gate
  // comes from the session `is_platform_admin` flag on /auth/me — neither is
  // org-scoped, so a same-tab user switch (not an org switch) is what must evict.
  platformOnboarding: {
    access: (userId: string) => ['fieldwatch-v2', 'platform-onboarding', 'access', userId] as const,
    summary: (userId: string) => ['fieldwatch-v2', 'platform-onboarding', 'summary', userId] as const,
  },
  // GOAL-UI-000 PR-AR: V2 platform-admin device health diagnostic. The access gate
  // is keyed by USER (the /auth/me is_platform_admin flag, not org-scoped); the
  // per-device health read is keyed by user + device (the admin route is not
  // org-scoped and `device_id` is the MQTT identity string). PR-BD adds the sibling
  // per-device component-inventory read under the same gate, keyed the same way.
  adminDeviceHealth: {
    access: (userId: string) => ['fieldwatch-v2', 'admin-device-health', 'access', userId] as const,
    detail: (userId: string, deviceId: string) =>
      ['fieldwatch-v2', 'admin-device-health', 'detail', userId, deviceId] as const,
    components: (userId: string, deviceId: string) =>
      ['fieldwatch-v2', 'admin-device-health', 'components', userId, deviceId] as const,
  },
  // GOAL-UI-000 PR-AT: V2 platform-admin equipment-diagnostic summary diagnostic.
  // The access gate is keyed by USER (the /auth/me is_platform_admin flag); the
  // summary read is keyed by user + org because it passes the CURRENT org id as the
  // explicit ?org_id= query parameter, so an org switch (or same-tab user switch)
  // must evict the cached aggregate counts.
  platformEquipmentDiagnostic: {
    access: (userId: string) =>
      ['fieldwatch-v2', 'platform-equipment-diagnostic', 'access', userId] as const,
    summary: (userId: string, orgId: string) =>
      ['fieldwatch-v2', 'platform-equipment-diagnostic', 'summary', userId, orgId] as const,
    // PR-AZ: the GLOBAL ADR-050 template catalog is NOT org-scoped, so the masked
    // aggregate is keyed by USER only (the platform-admin gate); a same-tab user
    // switch must still evict the prior identity's view.
    templates: (userId: string) =>
      ['fieldwatch-v2', 'platform-equipment-diagnostic', 'templates', userId] as const,
  },
  // GOAL-UI-000 PR-AV: V2 platform-admin asset→primary-device diagnostic. The
  // access gate is keyed by USER (the /auth/me is_platform_admin flag); the
  // per-asset read is keyed by user + org + asset because the admin route is
  // org-scoped in its path, so an org switch (or same-tab user switch) must evict
  // the cached masked view.
  adminPrimaryDevice: {
    access: (userId: string) => ['fieldwatch-v2', 'admin-primary-device', 'access', userId] as const,
    detail: (userId: string, orgId: string, assetId: string) =>
      ['fieldwatch-v2', 'admin-primary-device', 'detail', userId, orgId, assetId] as const,
  },
  // GOAL-UI-000 PR-BF: V2 platform-admin security-event hash-chain status. The access
  // gate is keyed by USER (the /auth/me is_platform_admin flag); the chain-status read
  // is keyed by user + org because the CURRENT org id rides as the explicit ?org_id=
  // query parameter, so an org switch (or same-tab user switch) must evict the cached
  // masked status.
  platformEventChainStatus: {
    access: (userId: string) =>
      ['fieldwatch-v2', 'platform-event-chain-status', 'access', userId] as const,
    status: (userId: string, orgId: string) =>
      ['fieldwatch-v2', 'platform-event-chain-status', 'status', userId, orgId] as const,
  },
} as const;

// A1 observed-NDVI (PR #409 audit amendment): centralized PURE key family.
// Org identity is passed in explicitly by the adapter — this module must not
// import auth/getOrgId. Tuple shape is byte-identical to the prior
// adapter-local family: namespace + org + field + inclusive UTC window.
export const observedNdviKeys = {
  series: (orgId: string, fieldId: string, fromDate: string, toDate: string) =>
    ['fieldwatch-v2', 'agronomics', 'observed-ndvi', orgId, fieldId, fromDate, toDate] as const,
} as const;

// A2 atmospheric balance (ratification 426eb2fe): centralized PURE key family.
// Org identity is passed in explicitly by the adapter — this module must not
// import auth/getOrgId. Identity is exactly org + field: a field switch rotates
// the key, so the prior field's response can never be surfaced.
// V2 agronomics leaf-wetness evidence (week02). The key carries BOTH the org
// and the field captured at call time: the legacy `fieldSensors.byField` key
// omits org identity, so the same field id read under two orgs would collide on
// one cache entry. Distinct `fieldwatch-v2` namespace so this narrow
// value/unit/observedAt projection never shares an entry with the legacy
// raw-shaped device/sensor caches.
export const leafWetnessKeys = {
  evidence: (orgId: string, fieldId: string) =>
    ['fieldwatch-v2', 'leaf-wetness', orgId, fieldId] as const,
} as const;

export const atmosphericBalanceKeys = {
  balance: (orgId: string, fieldId: string) =>
    ['fieldwatch-v2', 'atmospheric-balance', orgId, fieldId] as const,
} as const;

// ADR-134 device location (Konum tab). A dedicated `fieldwatch-v2` family, and
// deliberately NOT an entry under `queryKeys.devices.*`: that whole legacy family
// (`detail`/`twin`/`sensors`) keys on the device id alone while its request paths
// embed the org, so the same public `device_id` read under two orgs collides on
// one cache entry. `device_id` is a public locator, not a tenant-unique id — the
// same string legitimately exists in two orgs — and nothing in this app clears the
// query cache on a tenant transition (`setOrgId` is a bare localStorage write;
// there is no `queryClient.clear()`/`removeQueries` anywhere in `src/`). So for a
// coordinate surface the org segment here is not defence in depth, it is the
// identity itself.
//
// Org identity is passed in explicitly by the adapter — this module must not
// import auth/getOrgId — and the adapter reads it back OUT of the key inside its
// query function, so the request path and the cache entry can never be built from
// two different reads of ambient storage.
//
// The history cursor is deliberately absent: history is one `useInfiniteQuery`
// whose pages live under a single entry, and the opaque cursor is authenticated
// but not encrypted (it carries observation/receipt instants), so it stays private
// adapter state and never becomes part of a cache address.
export const deviceLocationKeys = {
  latest: (orgId: string, deviceId: string) =>
    ['fieldwatch-v2', 'device-location', orgId, deviceId, 'latest'] as const,
  history: (orgId: string, deviceId: string) =>
    ['fieldwatch-v2', 'device-location', orgId, deviceId, 'history'] as const,
} as const;

export type DeviceLocationLatestKey = ReturnType<typeof deviceLocationKeys.latest>;
export type DeviceLocationHistoryKey = ReturnType<typeof deviceLocationKeys.history>;
